import { useEffect, useState, useRef } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import styled, { css } from 'styled-components';
import Button from 'components/ui-components/Button';
import { Arrow } from 'assets/icons';
import { white } from 'constants/variables';
import toRem from 'utils/toRem';
import responsive from 'utils/responsive';

const Carousel = ({ slides, columns, isFullHeight = false, ...rest }) => {
  const [isOnStart, setIsOnStart] = useState(true);
  const [isOnEnd, setIsOnEnd] = useState(false);

  const carouselRef = useRef(null);
  const carousel = carouselRef.current?.splide;

  const nextSlide = () => {
    carousel?.go('>');
  };

  const prevSlide = () => {
    carousel?.go('<');
  };

  useEffect(() => {
    if (carousel) {
      const {
        Components: {
          Controller: { getIndex: currentPage, getEnd: endPage },
        },
      } = carousel;

      carousel?.on('move', () => {
        setIsOnStart(currentPage() === 0);
        setIsOnEnd(currentPage() === endPage());
      });
    }

    return () => {
      carousel?.off('move');
    };
  }, [carousel]);

  const splideOptions = {
    perPage: 1,
    perMove: 1,
    pagination: false,
    arrows: false,
    gap: '24px',
    trimSpace: false,
    mediaQuery: 'min',
    fixedWidth: 'calc(100% - 3.5rem)',
    breakpoints: isFullHeight
      ? {
          768: {
            fixedHeight: '100%',
            fixedWidth: false,
          },
          980: {
            autoWidth: true,
          },
        }
      : {
          768: {
            perPage: 3,
            fixedWidth: false,
          },
          1025: {
            perPage: columns,
          },
        },
  };

  return (
    <>
      <CarouselWrapper isFullHeight={isFullHeight} {...rest}>
        <MainCarousel
          options={splideOptions}
          role="group"
          aria-label="Image carousel"
          ref={carouselRef}
          isFullHeight={isFullHeight}>
          {slides.length > 0 &&
            slides.map(({ content, key }, i) => (
              <SplideSlide
                className={`splide__slide splide__slide--${i}`}
                key={key}>
                {content}
              </SplideSlide>
            ))}
        </MainCarousel>
      </CarouselWrapper>

      <CarouselNavigation isFullHeight={isFullHeight}>
        <CarouselButton
          className="cef-btn cef-btn--red cef-btn--feature"
          handleClick={prevSlide}
          disabled={isOnStart}
          label="Previous slide">
          <Arrow fillColor={white} direction="left" />
        </CarouselButton>
        <CarouselButton
          className="cef-btn cef-btn--red cef-btn--feature"
          handleClick={nextSlide}
          disabled={isOnEnd}
          label="Next slide">
          <Arrow fillColor={white} direction="right" />
        </CarouselButton>
      </CarouselNavigation>
    </>
  );
};

const CarouselWrapper = styled.div`
  ${({ isFullHeight }) =>
    isFullHeight &&
    css`
      ${responsive('tabletSmall')} {
        height: 0;
        position: relative;
        padding-bottom: min(500px, 100%);
      }
    `}
`;

const MainCarousel = styled(Splide)`
  ${({ isFullHeight }) =>
    isFullHeight &&
    css`
      ${responsive('tabletSmall')} {
        width: calc(50vw - (48px / 2));
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
      }
    `}

  .splide__track {
    width: calc(100% + 10px);
    height: 100%;

    ${responsive('tabletSmall')} {
      width: 100%;
    }
  }

  .splide__list {
    height: 100%;
  }
`;

const CarouselNavigation = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${toRem(8)};
  margin-top: ${toRem(24)};

  ${({ isFullHeight }) =>
    isFullHeight &&
    css`
      ${responsive('tabletSmall')} {
        position: absolute;
        left: 0;
        bottom: ${toRem(56)};
      }
    `}
`;

const CarouselButton = styled(Button)`
  padding: 0;
  height: 48px;
  width: 48px;

  svg {
    margin: 0;
  }
`;

export default Carousel;
